import createCrudService from '@src/core/api/create-crud-service';
import { QUERY_KEYS } from '@src/shared/constants/query-keys';
import { request } from '@core/api/request';
import { queryClient } from '@core/api/query-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import type {
  TAnnouncement,
  TAnnouncementMediaKeyPayload,
  TAnnouncementMediaUploadUrlRequest,
  TAnnouncementMediaUploadUrlResponse,
  TAnnouncementPayload,
  TAnnouncementQuery,
  TAnnouncementStats,
  TAnnouncementDetailStats,
} from '@src/shared/types/announcement';

const invalidateAnnouncements = () => {
  queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.getAnnouncements],
  });
  queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.getAnnouncementStats],
  });
  queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.getAnnouncementDetailStats],
  });
};

const announcementsApi = {
  ...createCrudService<TAnnouncement, TAnnouncementQuery, TAnnouncementPayload>(
    {
      queryKey: [QUERY_KEYS.getAnnouncements],
      url: 'announcements',
      validateQuery: [QUERY_KEYS.getAnnouncementStats],
    }
  ),

  useGetStats: (
    organization?: number | string,
    options?: Partial<UseQueryOptions<TAnnouncementStats>>
  ) =>
    useQuery({
      queryKey: [QUERY_KEYS.getAnnouncementStats, organization],
      queryFn: async () =>
        await request<TAnnouncementStats>({
          url: 'announcements/stats/',
          method: 'GET',
          params: { organization },
        }),
      enabled: Boolean(organization),
      staleTime: 60000,
      ...options,
    }),

  useGetDetailStats: (
    id?: number,
    options?: Partial<UseQueryOptions<TAnnouncementDetailStats>>
  ) => {
    const { enabled = true, ...rest } = options ?? {};
    return useQuery({
      queryKey: [QUERY_KEYS.getAnnouncementDetailStats, id],
      queryFn: async () =>
        await request<TAnnouncementDetailStats>({
          url: `announcements/${id}/stats/`,
          method: 'GET',
        }),
      staleTime: 60000,
      ...rest,
      enabled: Boolean(id) && enabled,
    });
  },

  useGetMediaUploadUrl: () =>
    useMutation({
      mutationFn: async (body: TAnnouncementMediaUploadUrlRequest) =>
        await request<TAnnouncementMediaUploadUrlResponse>({
          url: 'announcements/media/upload-url/',
          method: 'POST',
          body,
        }),
      onError: (error: string) => {
        toast.error(error);
      },
    }),

  useAttachMedia: () =>
    useMutation({
      mutationFn: async ({ id, key }: { id: number; key: string }) =>
        await request<TAnnouncement>({
          url: `announcements/${id}/media/attach/`,
          method: 'POST',
          body: { key } satisfies TAnnouncementMediaKeyPayload,
        }),
      onSuccess: () => {
        invalidateAnnouncements();
      },
      onError: (error: string) => {
        toast.error(error);
      },
    }),

  useReplaceMedia: () =>
    useMutation({
      mutationFn: async ({ id, key }: { id: number; key: string }) =>
        await request<TAnnouncement>({
          url: `announcements/${id}/media/replace/`,
          method: 'PATCH',
          body: { key } satisfies TAnnouncementMediaKeyPayload,
        }),
      onSuccess: () => {
        invalidateAnnouncements();
      },
      onError: (error: string) => {
        toast.error(error);
      },
    }),

  useRemoveMedia: () =>
    useMutation({
      mutationFn: async ({ id }: { id: number }) =>
        await request<void>({
          url: `announcements/${id}/media/remove/`,
          method: 'DELETE',
        }),
      onSuccess: () => {
        invalidateAnnouncements();
      },
      onError: (error: string) => {
        toast.error(error);
      },
    }),

  useReorder: () =>
    useMutation({
      mutationFn: async (ids: number[]) =>
        await request({
          url: 'announcements/reorder/',
          method: 'POST',
          body: { ids },
        }),
      onSuccess: () => {
        invalidateAnnouncements();
      },
      onError: (error: string) => {
        toast.error(error);
      },
    }),
};

export default announcementsApi;
